/**
 * The hours of the redesigned project dashboard, everywhere it shows them —
 * "Työtunnit tänään" of the site (ProjectOverviewWorksite) and the user's
 * own day, week and month (ProjectOverviewMyDay): the time of the work
 * records of their own (isWorkTimeRecord) less that of the meal breaks under
 * them (BREAK_MEAL, isMealBreakRecord). Coffee breaks, other breaks and rides
 * stay in the work's time. This is the rule of the server's salary reports
 * (a clocked meal break is the day's reduction; generic BREAK and coffee
 * breaks are not reduced) and of the partner integration.
 *
 * The site's figure is the server's day total (GET
 * projects/:projectId/record-total-hours, `day.totalHours`: the clocked-out
 * work records' hours today by the same rule) with the page's live part
 * added: the work records still running, and those clocked out after the
 * server summed the day — each from its clock-in, or the start of today, to
 * its clock-out or now, less its meal breaks in that time, a running one up
 * to now (workTimeBetweenMs). A running meal break so holds the hours still.
 */

/** An hour, in milliseconds. */
export const HOUR_MS = 60 * 60 * 1000;

const parseTime = (value) => (value ? Date.parse(value) : NaN);

/**
 * The start of the local day of `now`: its midnight, in epoch ms.
 * @param {number} now epoch ms
 * @returns {number}
 */
export const startOfDayMs = (now) => {
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
};

// The types of work records; a record with no type is an old work record.
const WORK_TYPES = ['WORK', 'PROJECTWORK', undefined, null, ''];

/**
 * Whether a record is timed as work: a work record (WORK, PROJECTWORK) of its
 * own — not one under another record — that is not deleted, not a date-only
 * stand-in for another user's record (whose times are made up) and has a
 * clock-in time. Breaks, rides, absences, items and the rest are not.
 * @param {Object} record
 * @returns {boolean}
 */
export const isWorkTimeRecord = (record) => {
  if (!record || record.deletedAt || record.parentRecordId) return false;
  if (record.isDateOnlyRecord) return false;
  if (!WORK_TYPES.includes(record.type)) return false;
  return Number.isFinite(parseTime(record.clockInAt));
};

/**
 * Whether a record is a meal break (BREAK_MEAL), the break the hours leave
 * out: one under a work record, not deleted, with a clock-in time.
 * @param {Object} record
 * @returns {boolean}
 */
export const isMealBreakRecord = (record) =>
  Boolean(record) &&
  !record.deletedAt &&
  record.type === 'BREAK_MEAL' &&
  Boolean(record.parentRecordId) &&
  Number.isFinite(parseTime(record.clockInAt));

/**
 * Whether a record is running: clocked in and not clocked out.
 * @param {Object} record
 * @returns {boolean}
 */
export const isRunningRecord = (record) =>
  record.status === 'IN' && !record.clockOutAt;

/**
 * Whether a record has been clocked out, at a known time.
 * @param {Object} record
 * @returns {boolean}
 */
export const isFinishedRecord = (record) =>
  record.status === 'OUT' && Number.isFinite(parseTime(record.clockOutAt));

/**
 * The meal breaks among `records`, by the id of the work record each stands
 * under (its `parentRecordId`).
 * @param {Object[]} records
 * @returns {Map<string, Object[]>}
 */
export const mealBreaksByWork = (records) =>
  (records || []).reduce((byWork, record) => {
    if (isMealBreakRecord(record)) {
      byWork.set(record.parentRecordId, [
        ...(byWork.get(record.parentRecordId) || []),
        record,
      ]);
    }
    return byWork;
  }, new Map());

/**
 * A work record's hours from `from` up to `now`, in ms: its time in that
 * span — from its clock-in, or `from` when it began earlier, to its
 * clock-out, or to `now` while it runs — less the time of its meal breaks
 * (`mealBreaks`) within that same time: each from its clock-in to its
 * clock-out, a running one to `now`. Meal breaks that overlap count once.
 * @param {Object} work a work record (isWorkTimeRecord)
 * @param {Object[]} mealBreaks its meal breaks (mealBreaksByWork)
 * @param {number} from epoch ms
 * @param {number} now epoch ms
 * @returns {number}
 */
export const workTimeBetweenMs = (work, mealBreaks, from, now) => {
  const start = Math.max(parseTime(work.clockInAt), from);
  const end = isRunningRecord(work)
    ? now
    : Math.min(parseTime(work.clockOutAt), now);
  if (!(end > start)) return 0;

  const spans = (mealBreaks || [])
    .map((meal) => [
      Math.max(parseTime(meal.clockInAt), start),
      Math.min(isRunningRecord(meal) ? now : parseTime(meal.clockOutAt), end),
    ])
    .filter(([spanStart, spanEnd]) => spanEnd > spanStart)
    .sort((a, b) => a[0] - b[0]);
  let mealMs = 0;
  let reached = start;
  spans.forEach(([spanStart, spanEnd]) => {
    const counted = Math.max(spanStart, reached);
    if (spanEnd > counted) {
      mealMs += spanEnd - counted;
      reached = spanEnd;
    }
  });
  return end - start - mealMs;
};

/**
 * The hours of work records today up to `now`, in ms: the sum over them of
 * workTimeBetweenMs from the start of today.
 * @param {Object[]} works work records (isWorkTimeRecord)
 * @param {Map<string, Object[]>} mealBreaks their meal breaks, by work record
 *   (mealBreaksByWork)
 * @param {number} now epoch ms
 * @returns {number}
 */
export const workTimeTodayMs = (works, mealBreaks, now) => {
  const dayStart = startOfDayMs(now);
  return works.reduce(
    (sum, work) =>
      sum +
      workTimeBetweenMs(work, mealBreaks?.get(work.id) || [], dayStart, now),
    0
  );
};

/**
 * A running time as hours, minutes and seconds: "1:23:45", "27:05:09".
 * @param {number} ms
 * @returns {string}
 */
export const formatRunningTime = (ms) => {
  const seconds = ms > 0 ? Math.floor(ms / 1000) : 0;
  const pad = (value) => String(value).padStart(2, '0');
  return `${Math.floor(seconds / 3600)}:${pad(
    Math.floor(seconds / 60) % 60
  )}:${pad(seconds % 60)}`;
};
